// Utilities
import { GraphicElementApi } from '@/types/GraphicElement';
import type { BackgroundPatternRecord } from '@/types/BackgroundPattern';
import Layer, { GroupLayer } from '@/types/Layer';
import { PendingDesign, Save } from '@/types/Save';
import { isLayerDraggable, isThemeLayer } from '@/utils/layer';
import { LayerMapper } from '@/utils/mappers/LayerMapper';
import { toZoneCoordinates } from '@/utils/zone';
import { zoneAllowsMethod } from '@/utils/zoneMethods';
import { defineStore } from 'pinia';
import { Engine } from '@/engine/core/Engine';
import { useVdpStore } from '@/stores/vdp';
const { t } = i18n.global;

export const useStageStore = defineStore('stage', {
    state: () => ({
        drawer: {
            isOpen: false as boolean,
            preventClose: false as boolean,
            expandedView: false as boolean
        },
        currentStep: 0 as number,
        // Compteur incrémenté à chaque réception du PostMessage `vdp:true` (hôte) :
        // déclenche l'affichage du guide VDP (VdpGuideModal, une fois par session).
        vdpGuideTrigger: 0 as number,
        // Parcours SIMPLIFIÉ (déclenché quand on choisit un thème dans le tunnel) :
        // studio épuré thème → VDP → panier, sans rail ni liste de calques. Le bouton
        // « Mode Expert » (choix définitif) rebascule sur le studio complet.
        simplifiedMode: false as boolean,
        simplifiedStep: 'theme' as 'theme' | 'vdp',
        configuration: {
            selectedMethod: null as string | null,
            categoryId: null as string | null,
            productId: null as string | null,
            patternId: null as string | null,
            patternIdByZone: {} as Record<string, string | null>,
            // Fond décoratif par zone (réécriture backpattern) : source de vérité
            // sérialisable. Le rendu est un pur fond de canvas de zone (rect
            // (0,0,zoneW,zoneH) rempli du motif répété, envoyé au fond) — aucune
            // position absolue, donc aucun bug d'offset négatif / z-order. Cf.
            // useBackgroundPattern. Remplace l'ancien élément moteur BackPattern.
            patternByZone: {} as Record<string, BackgroundPatternRecord | null>,
            graduationId: null as string | null,
            themeId: null as string | null,
            themeSku: null as string | null,
            view2d: false as boolean,
            dualView: false as boolean,
            dualViewSwapped: false as boolean,
            showZoneBorders: true as boolean,
            preventCanvasEdition: false as boolean
        },
        isLoading: false as boolean,
        /**
         * Deep-link « produit + thème » en cours d'ouverture : l'utilisateur est
         * encore sur la dernière page du tunnel (choix de thème) pendant que le
         * studio se prépare (chargement 3D → fetch + application du thème → push
         * vers customize.themeEdit). On l'utilise pour afficher un loader plein
         * écran à la place du sélecteur (qui induirait en erreur) — y compris
         * pendant le gap réseau du fetch thème où `isLoading` retombe à false.
         * Cf. useThemeLoader.preload + layouts/default.vue.
         */
        deeplinkThemePending: false as boolean,
        loadingMessage: t('Loading') as string,
        /**
         * Liste ordonnee des etapes affichees dans le loader fullscreen.
         * Vide = mode loader simple (un seul spinner + message).
         * Remplie = loader "multi-etapes" qui affiche la progression.
         */
        loadingSteps: [] as string[],
        /** Index de l'etape en cours (-1 = aucune active, length = toutes terminees). */
        loadingStepIndex: -1 as number,
        /**
         * Progression 0-100 affichee sous forme de barre quand definie
         * (typiquement pour le download du modele 3D). null = pas de
         * barre, le loader reste sur son mode spinner/steps.
         */
        loadingProgress: null as number | null,
        /** Controle global de la modal "ajouter un calque" (+ sur la layer bar et + sur la toolbar). */
        isAddLayerSheetOpen: false as boolean,
        /**
         * Etat du "preview de theme" actif. Quand un user selectionne un
         * theme depuis le step 3, on l'applique sur le canvas principal,
         * on garde un snapshot (historic index + content) et on ouvre un
         * bandeau flottant avec [Garder] / [Annuler]. Le snapshot permet
         * le rollback fidele si l'user annule.
         */
        themePreview: {
            active: false as boolean,
            themeName: '' as string,
            savedHistoricIndex: -1 as number,
            savedState: null as any[] | null,
            /**
             * Liste des themes (filtres categorie courante) dans l'ordre
             * visible dans la modal au moment du preview. Sert uniquement
             * pour la vignette "previewing" en UI — pas de cyclage ici.
             */
            themeList: [] as any[],
            currentIndex: -1 as number,
            /**
             * Controle la visibilite de la modal step 3 pendant un preview.
             * Sur mobile, on masque la sheet (translate Y 100%) pour laisser
             * la 3D pleine hauteur. L'utilisateur peut la rouvrir via le
             * bouton "Choisir un autre thème" dans le bandeau flottant.
             * Sur desktop (≥1024px) la sheet est un panneau lateral droit
             * qui reste visible sans gener la 3D, donc ce flag est ignore.
             */
            sheetVisible: true as boolean
        },
        showGuides: false as boolean,
        /** Affiche une grille en surimpression sur le canvas 2D (aide visuelle). */
        showGrid: false as boolean,
        /** Affiche des regles (graduees en pixels) en haut et a gauche du canvas. */
        showRulers: false as boolean,
        /**
         * Panneau de debug sur le canvas 2D : dimensions de la zone
         * selectionnee + taille/position/police de chaque objet texte.
         * Activable aussi via le query param `?debug=1`.
         */
        showDebugInfo: false as boolean,
        /**
         * Active le magnetisme sur les deplacements : un objet approchant
         * les centres du canvas / les axes des autres objets / les lignes
         * de grille dans un rayon de 8px s'y accroche.
         */
        snappingEnabled: false as boolean,
        fonts: [] as string[],
        layers: [] as Layer[],
        /**
         * Regroupe les layers qui portent `options.themeTag` (pose par
         * useThemeLoader sur chaque element issu d'un theme BO). La LayerBar
         * affiche UN SEUL chip par entree ici (au lieu de N chips pour
         * chaque enfant), et le form d'edition du theme liste ces enfants
         * groupes par type (texts, images, colorGroups).
         */
        themeAggregates: [] as Array<{ id: string; name: string; children: Layer[] }>,
        /**
         * Themes explicitement verrouilles par l'utilisateur via le bouton
         * cadenas de la LayerBar. Lecture seule cote UI : sert a distinguer
         * un verrou "initial" (pose par `useThemeLoader` quand
         * `isThemeLockedOnLoad=true`) d'un verrou INTENTIONNEL.
         *
         * LayerFormThemeEdit auto-deverrouille les enfants a l'ouverture
         * du form (indispensable pour permettre le drag cote canvas), mais
         * SI `userLockedThemes.has(aggregateId)`, on respecte l'intention
         * utilisateur et on skip le deverrouillage auto.
         */
        userLockedThemes: new Set<string>(),
        save: null as Save | null,
        pendingDesign: null as PendingDesign | null,
        graduationLayer: null as Layer | null,
        /**
         * Graduation à appliquer AUTOMATIQUEMENT à l'ouverture (défaut par site du
         * produit). Résolue au chargement produit, consommée une fois la scène 3D
         * prête (cf. useApplyGraduation / TresScene). L'utilisateur peut ensuite la
         * changer/retirer normalement.
         */
        defaultGraduationPending: null as string | null,
        patternLayer: null as Layer | null,
        patternLayersByZone: {} as Record<string, Layer>,
        themeLayer: null as GroupLayer | null,
        customerId: null as string | null,
        preventAutoRedirection: false as boolean,
        expectedVariantType: null as string | null,
        selectedZoneId: null as string | null,
        selectedCanvasMeshId: null as string | number | null
    }),
    actions: {
        toggleDrawer() {
            this.drawer.isOpen = !this.drawer.isOpen;
        },
        minimizeDrawer(forceAction: boolean = false) {
            if (this.drawer.preventClose || forceAction) {
                return;
            }
            this.drawer.isOpen = false;
        },
        selectCategory(id: string) {
            if (!id) {
                return;
            }
            this.configuration.categoryId = id;
        },
        selectProduct(id: string) {
            if (!id) {
                return;
            }
            this.configuration.productId = id;
        },
        goToSummary() {
            // @ts-ignore
            this.$router.push({ name: '//products/[productId]/summary' });
        },
        async resetChanges() {
            Engine.getInstance().content = [];
            // Le contenu moteur est vidé => plus AUCUN élément VDP sur le canvas.
            // Sans ce clear, le store VDP survivait au changement de produit : le
            // champ restait « actif » (widget, étape VDP du parcours simplifié) sans
            // exister sur le canvas, la quantité facturée restait le nombre de lignes
            // de l'ancien produit (useProductConfiguration lit vdpStore.lineCount),
            // et `fields` gardait des liaisons vers des uuid disparus — d'où des
            // objectId périmés à l'export (cf. bug « le PDF n'affiche que la dernière
            // entrée »). Les données VDP appartiennent à la création, pas à la session.
            useVdpStore().clear();
            if (Engine.getInstance().canvas?.canvas) {
                await Engine.getInstance().canvas.reload(Engine.getInstance().content);
            }
            this.updateLayers([]);
        },
        unloadProduct() {
            this.configuration.productId = null;
            // Changement de produit : purge l'état lié aux ZONES et aux thèmes,
            // sinon le nouveau produit hérite de pointeurs de zone périmés
            // (zones superposées) et d'agrégats de thème fantômes.
            this.selectedZoneId = null;
            this.selectedCanvasMeshId = null;
            this.patternLayersByZone = {};
            this.patternLayer = null;
            this.graduationLayer = null;
            this.themeLayer = null;
            this.themeAggregates = [];
            this.userLockedThemes = new Set();
            this.resetChanges();
        },
        isProductLoaded() {
            return !!this.configuration.productId;
        },
        toggleLoader(isLoading: boolean, message: string = '') {
            this.isLoading = isLoading;
            this.loadingMessage = message;
            if (!isLoading) {
                this.loadingSteps = [];
                this.loadingStepIndex = -1;
                this.loadingProgress = null;
            }
        },
        /**
         * Met a jour la barre de progression (0-100). `null` pour la
         * masquer. Conçu pour le suivi du download d'assets (modele 3D,
         * textures) ou toute autre operation avec un progres mesurable.
         */
        setLoadingProgress(progress: number | null) {
            this.loadingProgress = progress === null ? null : Math.max(0, Math.min(100, progress));
        },
        openAddLayerSheet() {
            // Auto-selectionne la zone la plus proche du point de vue
            // 3D courant avant d'ouvrir la modal, pour que les nouveaux
            // elements soient places la ou l'utilisateur regarde.
            this.autoSelectZoneFacingCamera();
            this.isAddLayerSheetOpen = true;
        },
        closeAddLayerSheet() {
            this.isAddLayerSheetOpen = false;
        },
        /**
         * Enregistre un snapshot de l'engine avant d'appliquer un theme
         * en preview. L'appelant doit ensuite lancer `processTheme`.
         * @param themeName label affiche dans le bandeau.
         * @param themeList themes navigables via prev/next (meme categorie).
         * @param currentIndex index du theme courant dans themeList.
         */
        startThemePreview(themeName: string, themeList: any[] = [], currentIndex: number = -1) {
            const hist = Engine.getInstance().historic;
            // Force un save pour avoir un snapshot propre avant le theme.
            hist.save();
            const savedIndex = hist.index;
            // On NE cache PAS le hydrated state ici : `engine.content` pointe
            // sur des instances que processTheme va muter (push/splice). Si on
            // gardait ce tableau, la prochaine restauration repartirait d'un
            // etat deja pollue par le theme precedent → empilement visuel.
            // A la place, on stocke uniquement l'index historic et on ré-hydrate
            // a la demande via `restoreSnapshot` (cf. setThemePreviewIndex).
            this.themePreview = {
                active: true,
                themeName,
                savedHistoricIndex: savedIndex,
                savedState: null,
                themeList: [...themeList],
                currentIndex,
                sheetVisible: false
            };
        },
        /**
         * Restaure l'engine a l'etat snapshote au debut de la preview, en
         * ré-hydratant les objets depuis l'historic (jamais le tableau cache,
         * cf. note dans startThemePreview). Reset aussi `themeId/themeSku`
         * pour que `processTheme` ne court-circuite pas le re-apply quand
         * l'utilisateur reclique sur le meme theme apres rollback.
         */
        restoreSnapshot() {
            const idx = this.themePreview.savedHistoricIndex;
            if (idx < 0) {
                return;
            }
            const engine = Engine.getInstance();
            engine.historic.index = idx;
            const fresh = engine.historic.getCurrentState();
            engine.content = fresh;
            engine.canvas?.reload?.(engine.content);
            this.updateLayers(engine.content);
            this.configuration.themeId = null;
            this.configuration.themeSku = null;
        },
        /** L'utilisateur garde le theme applique → simple reset du flag. */
        acceptThemePreview() {
            this.themePreview = {
                active: false,
                themeName: '',
                savedHistoricIndex: -1,
                savedState: null,
                themeList: [],
                currentIndex: -1,
                sheetVisible: true
            };
        },
        /**
         * Reaffiche la modal step 3 alors que la preview est toujours
         * active. Permet a l'utilisateur (mobile) de browser un autre
         * theme sans annuler le preview courant.
         */
        reopenPreviewSheet() {
            if (this.themePreview.active) {
                this.themePreview.sheetVisible = true;
            }
        },
        /**
         * Switch l'apercu a un autre theme de la meme categorie. Le
         * snapshot (savedHistoricIndex / savedState) reste inchange →
         * Annuler continue de fonctionner pour revenir a l'etat pre-
         * preview, quel que soit le nombre de switch.
         * Usage interne par previewPreviousTheme / previewNextTheme.
         * @returns le theme nouvellement selectionne (pour que le caller
         *          applique processTheme dessus).
         */
        setThemePreviewIndex(newIndex: number): any | null {
            const list = this.themePreview.themeList;
            if (!Array.isArray(list) || list.length === 0) {
                return null;
            }
            const idx = ((newIndex % list.length) + list.length) % list.length;
            const nextTheme = list[idx];
            if (!nextTheme) {
                return null;
            }
            // Restore engine a l'etat pre-preview (via ré-hydratation fraiche)
            // avant d'appliquer le nouveau theme, sinon les objets du theme
            // precedent restent sur le canvas → empilement visuel.
            this.restoreSnapshot();
            this.themePreview.currentIndex = idx;
            this.themePreview.themeName = nextTheme.name || 'Theme';
            return nextTheme;
        },
        /**
         * Prepare la preview d'un theme donne :
         * - Si aucune preview en cours : snapshot + mise a jour de currentIndex.
         * - Si preview active : rollback au snapshot original et mise a jour de
         *   l'index/name pour refleter le nouveau theme.
         * Le caller reste responsable d'appliquer le theme (processTheme).
         */
        prepareThemePreview(theme: any, themeList: any[] = [], currentIndex: number = -1) {
            if (!this.themePreview.active) {
                this.startThemePreview(theme.name || 'Theme', themeList, currentIndex);
                return;
            }
            this.restoreSnapshot();
            this.themePreview.themeName = theme.name || 'Theme';
            this.themePreview.themeList = [...themeList];
            this.themePreview.currentIndex = currentIndex;
            // L'utilisateur vient de choisir un nouveau theme depuis la modal :
            // on la masque a nouveau pour qu'il voie le preview sur la 3D.
            this.themePreview.sheetVisible = false;
        },
        /**
         * Rollback : restaure l'engine et force un re-render. On NE
         * passe PAS par Studio.rollback pour eviter une dependance
         * circulaire (Studio.ts appelle useStageStore() au top-level
         * → si stage.ts importe Studio, Pinia n'est pas encore pret
         * a l'init → _s undefined crash). On reimplemente le minimum
         * ici en utilisant uniquement Engine.
         */
        cancelThemePreview() {
            this.restoreSnapshot();
            this.themePreview = {
                active: false,
                themeName: '',
                savedHistoricIndex: -1,
                savedState: null,
                themeList: [],
                currentIndex: -1,
                sheetVisible: true
            };
        },
        /**
         * Trouve la zone dont la `cameraPosition` enregistree par
         * l'admin est la plus proche de la camera 3D actuelle, et
         * l'affecte comme zone selectionnee. Permet d'ajouter un
         * element directement "dans le champ de vision" sans que
         * l'utilisateur ait a choisir manuellement la zone.
         *
         * Silencieusement no-op si :
         *  - la scene 3D n'est pas encore prete (context.camera absent),
         *  - aucune zone n'a de cameraPosition renseignee,
         *  - le produit n'a qu'une zone (deja auto-selectionnee ailleurs).
         */
        autoSelectZoneFacingCamera() {
            try {
                const sceneStore = useSceneStore();
                const productStore = useProductStore();
                const ctxCam: any = sceneStore.context?.camera;
                // `context.camera` peut etre une instance Three.js directe
                // OU un ShallowRef (selon l'ordre d'init Tres). On
                // supporte les deux pour ne pas planter.
                const cam = ctxCam?.position ? ctxCam : ctxCam?.value;
                const camPos = cam?.position;
                if (!camPos) {
                    return;
                }

                const zones: any[] = [];
                productStore.product?.uvMapCanvasList?.forEach((c: any) => {
                    (c.zones ?? []).forEach((z: any) => {
                        // Seules les zones de la méthode active sont sélectionnables.
                        if (
                            z?.cameraPosition &&
                            zoneAllowsMethod(z.methods, productStore.selectedPrintProfile?.method)
                        ) {
                            zones.push({ ...z, canvasMeshId: c.meshId });
                        }
                    });
                });
                if (zones.length < 2) {
                    return;
                } // une seule zone = deja gere
                let best: any = null;
                let bestDist = Infinity;
                for (const z of zones) {
                    const cp = z.cameraPosition;
                    const dx = camPos.x - cp.x;
                    const dy = camPos.y - cp.y;
                    const dz = camPos.z - cp.z;
                    const d = dx * dx + dy * dy + dz * dz;
                    if (d < bestDist) {
                        bestDist = d;
                        best = z;
                    }
                }
                if (best && (this.selectedZoneId !== best.id || this.selectedCanvasMeshId !== best.canvasMeshId)) {
                    this.setSelectedZone(best.id, best.canvasMeshId);
                }
            } catch (err) {
                console.warn('[autoSelectZoneFacingCamera] failed', err);
            }
        },
        /**
         * Initialise le loader en mode multi-etapes. Affiche chaque
         * etape avec son statut (pending / active / done).
         * @param steps Liste ordonnee des labels
         * @param activeIndex Etape de depart (defaut 0)
         * @param message Titre general affiche au-dessus des etapes
         */
        startLoadingWithSteps(steps: string[], activeIndex: number = 0, message: string = '') {
            this.isLoading = true;
            this.loadingMessage = message;
            this.loadingSteps = [...steps];
            this.loadingStepIndex = activeIndex;
        },
        /**
         * Avance a l'etape suivante (ou a un index precis). Optionnellement
         * met a jour le label de l'etape courante avant de passer a la
         * suivante (utile pour les progressions intra-etape type "2/5").
         */
        advanceLoadingStep(nextIndex?: number, updatedCurrentLabel?: string) {
            if (updatedCurrentLabel && this.loadingStepIndex >= 0 && this.loadingStepIndex < this.loadingSteps.length) {
                this.loadingSteps[this.loadingStepIndex] = updatedCurrentLabel;
            }
            this.loadingStepIndex = typeof nextIndex === 'number' ? nextIndex : this.loadingStepIndex + 1;
        },
        setSelectedGraduationId(graduationId: string | null) {
            this.configuration.graduationId = graduationId;
        },
        toggleGuides() {
            this.showGuides = !this.showGuides;
        },
        setFonts(fonts: string[]) {
            this.fonts = fonts;
        },
        ensureElementZoneMetadata(graphicElementData: GraphicElementApi[]) {
            graphicElementData.forEach((graphicElement) => {
                if (!isLayerDraggable(graphicElement.type)) {
                    return;
                }

                const element = graphicElement as any;
                if (element.zoneId !== undefined && element.zoneId !== null) {
                    if (element.canvasMeshId === undefined || element.canvasMeshId === null) {
                        const existingZone = this.findProductZoneById(element.zoneId);
                        if (existingZone) {
                            element.canvasMeshId = existingZone.canvasMeshId;
                        }
                    }
                    return;
                }

                const inferredZone = this.inferElementZoneMetadata(element);
                if (!inferredZone) {
                    return;
                }

                element.zoneId = inferredZone.zoneId;
                element.canvasMeshId = inferredZone.canvasMeshId;
            });
        },
        findProductZoneById(zoneId: string | number) {
            const productStore = useProductStore();
            const canvases = productStore.product?.uvMapCanvasList ?? [];

            for (const canvas of canvases) {
                const zone = canvas.zones?.find((candidate: any) => String(candidate?.id) === String(zoneId));
                if (zone) {
                    return {
                        zone,
                        zoneId: zone.id,
                        canvasMeshId: canvas.meshId ?? zone.meshId ?? null
                    };
                }
            }

            return null;
        },
        getElementZoneCandidates(graphicElement: any) {
            const productStore = useProductStore();
            const candidates: Array<{
                zone: any;
                zoneId: string;
                canvasMeshId: string | number | null;
            }> = [];

            (productStore.product?.uvMapCanvasList ?? []).forEach((canvas: any) => {
                if (
                    graphicElement.canvasMeshId !== undefined &&
                    graphicElement.canvasMeshId !== null &&
                    String(canvas.meshId) !== String(graphicElement.canvasMeshId)
                ) {
                    return;
                }

                (canvas.zones ?? []).forEach((zone: any) => {
                    if (!zone?.id) {
                        return;
                    }
                    candidates.push({
                        zone,
                        zoneId: zone.id,
                        canvasMeshId: canvas.meshId ?? zone.meshId ?? null
                    });
                });
            });

            return candidates;
        },
        inferElementZoneMetadata(graphicElement: any): {
            zoneId: string;
            canvasMeshId: string | number | null;
        } | null {
            const zoneCandidates = this.getElementZoneCandidates(graphicElement);
            const center = this.getElementCanvasCenter(graphicElement);

            if (zoneCandidates.length === 0) {
                return null;
            }

            if (!center) {
                return zoneCandidates.length === 1 ? zoneCandidates[0] : null;
            }

            const matches = zoneCandidates
                .map((candidate) => {
                    const zoneCoords = toZoneCoordinates(center.x, center.y, candidate.zone);
                    const width = (candidate.zone.width || 0) * (candidate.zone.scaleX || 1);
                    const height = (candidate.zone.height || 0) * (candidate.zone.scaleY || 1);
                    const padding = 1;
                    const isInside =
                        zoneCoords.x >= -padding &&
                        zoneCoords.x <= width + padding &&
                        zoneCoords.y >= -padding &&
                        zoneCoords.y <= height + padding;

                    return {
                        ...candidate,
                        area: width * height,
                        isInside
                    };
                })
                .filter((candidate) => candidate.isInside)
                .sort((a, b) => a.area - b.area);

            if (matches.length > 0) {
                return matches[0];
            }

            return zoneCandidates.length === 1 ? zoneCandidates[0] : null;
        },
        getElementCanvasCenter(graphicElement: any): { x: number; y: number } | null {
            const rawPosition =
                graphicElement?.pos ?? graphicElement?.transform?.pos ?? graphicElement?.transform?._pos ?? null;
            const rawX = Number(rawPosition?.x ?? rawPosition?._x);
            const rawY = Number(rawPosition?.y ?? rawPosition?._y);

            if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) {
                return null;
            }

            const productStore = useProductStore();
            const engine = Engine.getInstance();
            const canvasWidth = engine.widthCanvas || productStore.product?.uvMapCanvasList?.[0]?.width || 1;
            const canvasHeight = engine.heightCanvas || productStore.product?.uvMapCanvasList?.[0]?.height || 1;
            const isNormalized = Math.abs(rawX) <= 1 && Math.abs(rawY) <= 1;

            return {
                x: isNormalized ? rawX * canvasWidth : rawX,
                y: isNormalized ? rawY * canvasHeight : rawY
            };
        },
        updateLayers(graphicElementData: GraphicElementApi[]) {
            this.ensureElementZoneMetadata(graphicElementData);

            const layers: Layer[] = [];
            this.graduationLayer = null;
            this.themeLayer = null;
            const newPatternsByZone: Record<string, Layer> = {};

            graphicElementData.forEach((graphicElement) => {
                const type = graphicElement.type;

                try {
                    if (isLayerDraggable(type)) {
                        const layer = LayerMapper.getLayerFromElement(graphicElement);
                        layers.push(layer);
                    } else if (isGraduationLayer(type)) {
                        this.graduationLayer = LayerMapper.getLayerFromElement(graphicElement);
                    } else if (isPatternLayer(type)) {
                        const zoneId = (graphicElement as any).zoneId || '__global__';
                        newPatternsByZone[zoneId] = LayerMapper.getLayerFromElement(graphicElement);
                    } else if (isThemeLayer(type)) {
                        this.themeLayer = <GroupLayer>LayerMapper.getLayerFromElement(graphicElement);
                    }
                } catch (error) {
                    console.error(`LayerMapper failed for ${type}:`, error);
                }
            });

            // Regroupement "theme" : les layers taggues options.themeTag
            // sortent de `layers` pour entrer dans `themeAggregates`. La
            // LayerBar affiche un chip unique par agregat et le form
            // d'edition plonge dedans pour lister texts/images/colors.
            const aggregatesMap = new Map<string, { id: string; name: string; children: Layer[] }>();
            const standaloneLayers: Layer[] = [];
            for (const layer of layers) {
                const themeTag = (layer as any).options?.themeTag as string | undefined;
                if (themeTag) {
                    if (!aggregatesMap.has(themeTag)) {
                        aggregatesMap.set(themeTag, {
                            id: themeTag,
                            name: (layer as any).options?.themeName || 'Theme',
                            children: []
                        });
                    }
                    aggregatesMap.get(themeTag)!.children.push(layer);
                } else {
                    standaloneLayers.push(layer);
                }
            }

            this.layers = standaloneLayers;
            this.themeAggregates = Array.from(aggregatesMap.values());
            this.patternLayersByZone = newPatternsByZone;

            // Nettoyage du mapping patternIdByZone: si une zone n'a plus de
            // pattern layer reel dans l'engine, on invalide son id configure.
            // Evite l'etat stale "pattern deja selectionne" apres suppression.
            const nextPatternByZone: Record<string, string | null> = { ...this.configuration.patternIdByZone };
            Object.keys(nextPatternByZone).forEach((zoneId) => {
                if (!newPatternsByZone[zoneId]) {
                    nextPatternByZone[zoneId] = null;
                }
            });
            this.configuration.patternIdByZone = nextPatternByZone;

            // Keep patternLayer as the pattern for the currently selected zone
            const currentZone = this.selectedZoneId || '__global__';
            this.patternLayer = newPatternsByZone[currentZone] || null;
        },
        updatePatternLayerForSelectedZone() {
            const currentZone = this.selectedZoneId || '__global__';
            this.patternLayer = this.patternLayersByZone[currentZone] || null;
        },
        updateConfiguration() {
            if (Object.keys(this.patternLayersByZone).length === 0) {
                this.configuration.patternId = null;
                this.configuration.patternIdByZone = {};
            }

            if (!this.themeLayer) {
                this.configuration.themeId = null;
            }

            if (!this.graduationLayer) {
                this.configuration.graduationId = null;
            }
        },
        resetConfigurationForStep(step: number) {
            const commonDefaults = {
                selectedMethod: null,
                patternId: null,
                patternIdByZone: {},
                graduationId: null,
                themeId: null,
                themeSku: null,
                showZoneBorders: true
            };

            const defaultsMap = new Map<number, any>([
                [
                    0,
                    {
                        ...commonDefaults,
                        categoryId: null,
                        productId: null,
                        showZoneBorders: true,
                        preventCanvasEdition: false
                    }
                ],
                [
                    1,
                    {
                        ...commonDefaults,
                        categoryId: null,
                        productId: null,
                        showZoneBorders: true,
                        preventCanvasEdition: false
                    }
                ],
                [
                    2,
                    {
                        ...commonDefaults,
                        showZoneBorders: true
                    }
                ]
            ]);

            Object.assign(this.configuration, defaultsMap.get(step));
        },
        setSelectedZone(zoneId: string | null, canvasMeshId: string | number | null) {
            this.selectedZoneId = zoneId;
            this.selectedCanvasMeshId = canvasMeshId;
            this.updatePatternLayerForSelectedZone();
        }
    }
});
